import { cn } from '@/lib/utils';
import type { Locale } from '@/lib/i18n/config';

/**
 * Inline SVG flag icons. Reasons for not using emoji flags:
 *   - Windows has no native emoji flag glyphs (renders the letter pair ES / US / BR).
 *   - Some corporate Linux fonts also lack them.
 * The three flags below are intentional simplifications of the official designs
 * — recognizable at small sizes, no heraldic detail needed for a language picker.
 */
type Props = {
  locale: Locale;
  className?: string;
  title?: string;
};

const TITLES: Record<Locale, string> = {
  es: 'España',
  pt: 'Brasil',
  en: 'United States',
};

export function FlagIcon({ locale, className, title }: Props) {
  const label = title ?? TITLES[locale];
  const baseClass = cn(
    'inline-block h-4 w-6 shrink-0 overflow-hidden rounded-sm border border-black/10 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]',
    className
  );

  if (locale === 'es') {
    return (
      <svg
        viewBox="0 0 750 500"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={label}
        className={baseClass}
      >
        <title>{label}</title>
        <rect width="750" height="500" fill="#AA151B" />
        <rect width="750" height="250" y="125" fill="#F1BF00" />
      </svg>
    );
  }

  if (locale === 'en') {
    // Simplified US flag: 13 alternating stripes + blue canton.
    return (
      <svg
        viewBox="0 0 760 400"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={label}
        className={baseClass}
      >
        <title>{label}</title>
        <rect width="760" height="400" fill="#B22234" />
        {[1, 3, 5, 7, 9, 11].map((i) => (
          <rect
            key={i}
            x="0"
            y={(400 / 13) * i}
            width="760"
            height={400 / 13}
            fill="#FFFFFF"
          />
        ))}
        <rect width="304" height={(400 / 13) * 7} fill="#3C3B6E" />
        {Array.from({ length: 9 }).flatMap((_, row) =>
          Array.from({ length: row % 2 === 0 ? 6 : 5 }).map((_, col) => {
            const cx = 304 / 12 + (col * 304) / 6 + (row % 2 === 1 ? 304 / 12 : 0);
            const cy = ((400 / 13) * 7) / 10 + (row * ((400 / 13) * 7)) / 10;
            return <circle key={`${row}-${col}`} cx={cx} cy={cy} r={6} fill="#FFFFFF" />;
          })
        )}
      </svg>
    );
  }

  // Brazil: green field, yellow rhombus, blue disc. The motto is intentionally
  // omitted — illegible at flag-icon size and adds noise.
  return (
    <svg
      viewBox="0 0 720 504"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
      className={baseClass}
    >
      <title>{label}</title>
      <rect width="720" height="504" fill="#009C3B" />
      <polygon points="360,42 678,252 360,462 42,252" fill="#FFDF00" />
      <circle cx="360" cy="252" r="115" fill="#002776" />
    </svg>
  );
}
